import React, { useState } from "react";
import { Typography, Space, Divider, Button } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import FinetuneSettings from "../../../shared/FinetuneSettings";
import { FinetuneDataset } from "../../../../model_gen/finetuning";

const { Title } = Typography;

interface FinetuneSettingsStepProps {
  exportPrompt: string;
  setExportPrompt: (value: string) => void;
  fourbit: boolean;
  setFourbit: (value: boolean) => void;
  baseModel: string;
  setBaseModel: (value: string) => void;
  promptDeletions: string[];
  setPromptDeletions: (value: string[]) => void;
  warmupSteps: number;
  setWarmupSteps: (value: number) => void;
  learningRate: number;
  setLearningRate: (value: number) => void;
  trainSplit: number;
  setTrainSplit: (value: number) => void;
  isExporting: boolean;
  selectedSubDataset?: FinetuneDataset | null;
  setSelectedSubDataset?: (dataset: FinetuneDataset | null) => void;
}

const FinetuneSettingsStep: React.FC<FinetuneSettingsStepProps> = ({
  exportPrompt,
  setExportPrompt,
  fourbit,
  setFourbit,
  baseModel,
  setBaseModel,
  promptDeletions,
  setPromptDeletions,
  warmupSteps,
  setWarmupSteps,
  learningRate,
  setLearningRate,
  trainSplit,
  setTrainSplit,
  isExporting,
  selectedSubDataset,
  setSelectedSubDataset,
}) => {
  const [advancedExpanded, setAdvancedExpanded] = useState<boolean>(false);
  
  // For intent group finetunes
  const intentGroupId: number | null = null;

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Title level={4}>Finetune Settings</Title>
      
      {/* Basic Settings */}
      <FinetuneSettings
        exportPrompt={exportPrompt}
        setExportPrompt={setExportPrompt}
        fourbit={fourbit}
        setFourbit={setFourbit}
        baseModel={baseModel}
        setBaseModel={setBaseModel}
        promptDeletions={promptDeletions}
        setPromptDeletions={setPromptDeletions}
        warmupSteps={warmupSteps}
        setWarmupSteps={setWarmupSteps}
        learningRate={learningRate}
        setLearningRate={setLearningRate}
        trainSplit={trainSplit}
        setTrainSplit={setTrainSplit}
        isExporting={isExporting}
        hideAdvancedSettings={true}
        intentGroupId={intentGroupId}
        selectedSubDataset={selectedSubDataset}
        setSelectedSubDataset={setSelectedSubDataset}
      />
      
      {/* Advanced Settings Section */}
      <div>
        <Divider 
          orientation="left" 
          style={{ margin: "24px 0 16px" }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Title level={5} style={{ margin: 0, marginRight: 8 }}>Advanced</Title>
          </div>
        </Divider>
        
        <Button 
          type="link" 
          onClick={() => setAdvancedExpanded(!advancedExpanded)}
          icon={advancedExpanded ? <UpOutlined /> : <DownOutlined />}
          style={{ marginBottom: 16, padding: 0 }}
        >
          {advancedExpanded ? "Hide advanced settings" : "Show advanced settings"}
        </Button>
        
        {advancedExpanded && (
          <FinetuneSettings
            exportPrompt={exportPrompt}
            setExportPrompt={setExportPrompt}
            fourbit={fourbit}
            setFourbit={setFourbit}
            baseModel={baseModel}
            setBaseModel={setBaseModel}
            promptDeletions={promptDeletions}
            setPromptDeletions={setPromptDeletions}
            warmupSteps={warmupSteps}
            setWarmupSteps={setWarmupSteps}
            learningRate={learningRate}
            setLearningRate={setLearningRate}
            trainSplit={trainSplit}
            setTrainSplit={setTrainSplit}
            isExporting={isExporting}
            showOnlyAdvancedSettings={true}
            intentGroupId={intentGroupId}
            selectedSubDataset={selectedSubDataset}
            setSelectedSubDataset={setSelectedSubDataset}
          />
        )}
      </div>
    </Space>
  );
};

export default FinetuneSettingsStep; 